// import type a propósito: services importa este archivo y este importaba services de vuelta.
// Ese ciclo hacía que, según qué módulo entrara primero, reservaCentralizadaApi llegara undefined
// a rootReducers y el store reventara al construirse. Con `type` la arista desaparece en runtime.
import type { GetBookingParams } from '../services/reservaCentralizada.services'
import { removerSaltosLines } from '../utils/removerSaltosLinea'

import {
  dateAfterMonths,
  dateBeforeMonths,
  // eslint-disable-next-line camelcase
  dateToYY_MM_DD,
} from '@/common/helpers/utilsDate'

// Regla de negocio: la ventana de fechas acota SOLO los estados finales (un mes atras, seis
// meses adelante), para que el tablero no arrastre reservas finalizadas viejas. Las que NO estan
// en estado final vienen TODAS, sin importar su FechaInicio: siguen pendientes de operar.
export const MESES_ATRAS = 1

export const MESES_ADELANTE = 6

const getReservasCentralizadasQueryOData = `
$select=CodigoReserva,ReservaCentralizadaId,ActividadMaestra,UrlCartaEnviada,UrlCartaEnviadaExcel,FechaUltimaSolicitudSalesforce,FechaInicio,FechaFin,FechaCreacion,FechaUltimaModificacion
&$expand=ReservaCentralizadaDetalle($select=ReservaCentralizadaId,ReservaCentralizadaDetalleId,FechaInicio,FechaFin,FechaCreacion,FechaUltimaModificacion,Actividad;$expand=ReservaCentralizadaBoletosDetalle($select=AsignacionPasajero;$expand=AsignacionPasajero($select=Pasajero;$expand=Pasajero($select=Nombre,Apellido))),Actividad($select=Producto;$expand=Componente($select=ComponenteId,Descripcion,Nombre,FechaCreacion),Producto($select=AcomodacionDescripcion)),EstadoFisicoReserva($select=EstadoFisicoReservaId,Nombre,EsEstadoFinal,EstadoLogicoVerticalReservaId,EstadoLogicoHorizontalReservaId;$expand=EstadoLogicoHorizontalReserva($select=Nombre,ColorEstadoLogico)))
&$expand=Proveedor($select=NombreComercial, ProveedorId)
&$expand=ActividadMaestra($select=Estado,Booking,FechaUltimaModificacion,FechaInicio;$expand=Booking($select=BookingId,BookingCode;$expand=Cliente($select=Nombre,ClienteId)),EjecutivoComercial($select=NombreCompleto),EjecutivoOperativo($select=NombreCompleto),Estado($select=Nombre),SalesProfit($select=Nombre,Valor))
&$expand=EstadoFisicoReserva($select=EstadoLogicoVerticalReservaId,EstadoLogicoHorizontalReservaId,EstadoFisicoReservaId,EsEstadoFinal,EstadoLogicoVerticalReserva;$expand=EstadoLogicoVerticalReserva($select=Nombre,EsEstadoVertical,EstadoLogicoReservaId),EstadoLogicoHorizontalReserva($select=Nombre,ColorEstadoLogico))
&$expand=ConfiguracionReserva($select=CartaNuevaReserva,CartaModificacionReserva,CartaCancelacionReserva,CartaRoomingListReserva,ConfiguracionTarjeta,Agrupacion;$expand=Agrupacion($select=Valor))
&$count=true
`

// La ventana acota SOLO los estados finales; se comparte entre el tablero completo y la
// consulta de una reserva puntual para que ambas muestren exactamente lo mismo.
const getFiltroVentanaEstadosFinales = (): string => {
  const desde = dateToYY_MM_DD(dateBeforeMonths(MESES_ATRAS))
  const hasta = dateToYY_MM_DD(dateAfterMonths(MESES_ADELANTE))

  return `(EstadoFisicoReserva/EsEstadoFinal eq false or (FechaInicio ge ${desde} and FechaInicio le ${hasta}))`
}

// El tablero se llena con UNA sola consulta. Antes eran dos llamadas (finales / no finales) y la
// de finales, además de la ventana de fechas, no traía el campo EsEstadoFinal, así que al mover
// una reserva a estado final la card desaparecía del tablero (BUG reserva 245). La regla de
// fechas se conserva, pero expresada como un OR dentro del mismo $filter en vez de como dos
// consultas: las no finales entran siempre, las finales solo dentro de la ventana. Si se vuelve
// a particionar por performance, la ÚNICA diferencia entre los filtros debe ser la dimensión que
// particiona.
// El rango se calcula en cada llamada, no al cargar el módulo: una pestaña abierta varios días
// se quedaba con la ventana del día que se montó.
// ponytail: sin paginación. Confirmado con backend: PageSize 10000 y emite @odata.nextLink
// (no trunca en silencio), y @odata.count es el total real previo al paginado. Hoy el menú más
// grande tiene 1270 filas. Si algún @odata.count se acerca a 10000, seguir el nextLink.
// IIS corta con 404.15 (Query String too long) sobre 2048 caracteres, y esta consulta ronda los
// 2000 sin codificar: cada espacio pasa a %20 y la empuja por encima del límite. Los saltos de
// línea del template dejan un espacio pegado a cada '&' que no aporta nada, así que se quitan.
const compactar = (query: string): string => removerSaltosLines(query).replace(/\s+&/g, '&').trim()

export const getQueryReservasOData = ({ id }: GetBookingParams): string => {
  const filtro = `&$filter=ConfiguracionReserva/MenuReservaId eq ${id?.toString() ?? ''} and ${getFiltroVentanaEstadosFinales()}`

  return compactar(`${getReservasCentralizadasQueryOData}${filtro}`)
}

// Una reserva puntual: refresco después de un cambio de estado, o una que el tablero todavía no
// tiene. Se acota al menú para no traer reservas de otro tablero. Comparte el mismo $select que
// el tablero, así la reserva refrescada se mapea igual — incluido el estado por línea, del que
// depende que la tarjeta se rompa en su mitad viva y su mitad Negada.
export const getQueryReservaPorIdOData = (reservaId: number, menuId: number): string => {
  // Sin la ventana de fechas, a diferencia del tablero. Dos razones: pedir una reserva concreta
  // por id ya la acota, y si el usuario acaba de operar sobre ella la quiere ver aunque caiga
  // fuera de la ventana. Además esos ~100 caracteres extra empujaban la URL sobre el límite de
  // IIS (404.15) y el refresco fallaba en silencio, dejando el tablero desactualizado.
  const filtro =
    `&$filter=ReservaCentralizadaId eq ${reservaId} and ConfiguracionReserva/MenuReservaId eq ${menuId}`

  return compactar(`${getReservasCentralizadasQueryOData}${filtro}`)
}
